import React, { useState } from 'react'
import { AppShell } from '@/components/shared/AppShell'
import { router, Link } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table'
import {
  Trash2,
  FolderTree,
  BookOpen,
  FileText,
  Plus,
  Search,
  X,
  GraduationCap,
  Sparkles,
  Layers,
  ChevronRight,
  ExternalLink,
  MoreVertical
} from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog'

interface Exam {
  id: number
  name: string
  code: string
  year: number | null
  is_active: boolean
  syllabi_count: number
  tests_count: number
}

interface Props {
  exams: Exam[]
}

const TAXONOMY_TABS = [
  { label: 'Exams', href: '/admin/exams', active: true, icon: GraduationCap },
  { label: 'Syllabi', href: '/admin/syllabi', active: false, icon: BookOpen },
  { label: 'Standards', href: '/admin/standards', active: false, icon: Layers },
  { label: 'Subjects', href: '/admin/subjects', active: false, icon: FolderTree },
  { label: 'Chapters', href: '/admin/chapters', active: false, icon: FileText },
  { label: 'Sections', href: '/admin/sections', active: false, icon: Layers },
]

export default function ExamsIndex({ exams }: Props) {
  const [name, setName] = useState('')
  const [code, setCode] = useState('')
  const [year, setYear] = useState<number | ''>(new Date().getFullYear())
  const [isActive, setIsActive] = useState(true)
  const [createExamModalOpen, setCreateExamModalOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim() || !code.trim()) return

    setSubmitting(true)
    router.post('/admin/exams', {
      exam: {
        name: name.trim(),
        code: code.trim().toUpperCase(),
        year: year === '' ? null : Number(year),
        is_active: isActive
      }
    }, {
      onSuccess: () => {
        setName('')
        setCode('')
        setYear(new Date().getFullYear())
        setIsActive(true)
        setCreateExamModalOpen(false)
      },
      onFinish: () => setSubmitting(false)
    })
  }

  const handleDelete = (id: number) => {
    if (!confirm('Are you sure you want to delete this exam? All associated blueprints and links will be affected.')) return
    router.delete(`/admin/exams/${id}`)
  }

  const filteredExams = exams.filter(e =>
    e.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    e.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (e.year && String(e.year).includes(searchQuery))
  )

  return (
    <AppShell>
      <div className="space-y-4 sm:space-y-6 max-w-6xl mx-auto w-full min-w-0 pb-12">
        {/* Header Area with Add Exam & 3-Dot Taxonomy Navigation */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 sm:gap-4">
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold font-serif tracking-tight text-foreground">
              Taxonomy Management
            </h1>
            <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
              Manage target exams, syllabi, standards, subjects, chapters, and sections
            </p>
          </div>

          <div className="flex items-center justify-between sm:justify-end gap-2 w-full sm:w-auto shrink-0">
            <Button
              size="sm"
              onClick={() => setCreateExamModalOpen(true)}
              className="h-8 sm:h-9 px-3 text-xs font-bold rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 shadow-2xs gap-1.5"
            >
              <Plus className="h-3.5 w-3.5" />
              <span>Add New Exam</span>
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 sm:h-9 w-8 sm:w-9 rounded-xl border-border/80 text-foreground hover:bg-muted shadow-2xs"
                  title="Taxonomy Modules"
                  aria-label="Taxonomy Modules"
                >
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 bg-card border-border shadow-glass-md rounded-xl p-1 z-50">
                <DropdownMenuLabel className="text-[10px] uppercase font-bold text-muted-foreground px-2 py-1">
                  Taxonomy Sections
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-border/60" />
                <DropdownMenuItem asChild className="cursor-pointer font-bold bg-muted/60 text-primary">
                  <Link href="/admin/exams" className="flex items-center gap-2 px-2 py-1.5 text-xs rounded-lg">
                    <GraduationCap className="h-3.5 w-3.5 text-primary" />
                    <span>Exams</span>
                    <span className="ml-auto text-[10px] text-primary font-bold">Active</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="cursor-pointer font-medium hover:bg-muted">
                  <Link href="/admin/syllabi" className="flex items-center gap-2 px-2 py-1.5 text-xs rounded-lg text-foreground">
                    <BookOpen className="h-3.5 w-3.5 text-secondary" />
                    <span>Syllabi</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="cursor-pointer font-medium hover:bg-muted">
                  <Link href="/admin/standards" className="flex items-center gap-2 px-2 py-1.5 text-xs rounded-lg text-foreground">
                    <Layers className="h-3.5 w-3.5 text-tertiary" />
                    <span>Standards</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="cursor-pointer font-medium hover:bg-muted">
                  <Link href="/admin/subjects" className="flex items-center gap-2 px-2 py-1.5 text-xs rounded-lg text-foreground">
                    <FolderTree className="h-3.5 w-3.5 text-primary" />
                    <span>Subjects</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="cursor-pointer font-medium hover:bg-muted">
                  <Link href="/admin/chapters" className="flex items-center gap-2 px-2 py-1.5 text-xs rounded-lg text-foreground">
                    <FileText className="h-3.5 w-3.5 text-secondary" />
                    <span>Chapters</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="cursor-pointer font-medium hover:bg-muted">
                  <Link href="/admin/sections" className="flex items-center gap-2 px-2 py-1.5 text-xs rounded-lg text-foreground">
                    <Layers className="h-3.5 w-3.5 text-tertiary" />
                    <span>Sections</span>
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Create New Target Exam Dialog */}
        <Dialog open={createExamModalOpen} onOpenChange={setCreateExamModalOpen}>
          <DialogContent className="w-[calc(100%-1.5rem)] max-w-md p-4 sm:p-6 rounded-2xl bg-card border-border shadow-glass-md">
            <DialogHeader className="text-left space-y-1">
              <DialogTitle className="text-base sm:text-lg font-bold text-foreground flex items-center gap-2">
                <GraduationCap className="h-4 w-4 sm:h-5 sm:w-5 text-primary shrink-0" />
                <span>Create New Target Exam</span>
              </DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground leading-relaxed">
                Define master taxonomy information for the new competitive examination.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleCreate} className="space-y-4 pt-1">
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-foreground">
                  Exam Name <span className="text-destructive">*</span>
                </label>
                <Input
                  placeholder="e.g. NISM Series V-A: Mutual Fund Distributors"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="rounded-xl border-border text-xs h-9 bg-background focus:ring-1 focus:ring-primary"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-foreground">
                    Code <span className="text-destructive">*</span>
                  </label>
                  <Input
                    placeholder="e.g. NISM_VA"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    className="rounded-xl border-border text-xs h-9 font-mono uppercase bg-background focus:ring-1 focus:ring-primary"
                    required
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-foreground">Year</label>
                  <Input
                    type="number"
                    placeholder="2026"
                    value={year}
                    onChange={(e) => setYear(e.target.value ? Number(e.target.value) : '')}
                    className="rounded-xl border-border text-xs h-9 bg-background"
                  />
                </div>
              </div>

              {/* Status Switch Card */}
              <div className="p-3 rounded-xl border border-border/70 bg-muted/30 flex items-center justify-between gap-3">
                <div>
                  <div className="text-xs font-bold text-foreground">Active for Students</div>
                  <div className="text-[10px] text-muted-foreground">Visible to candidates in exam catalog</div>
                </div>
                <Switch
                  checked={isActive}
                  onCheckedChange={setIsActive}
                  className="shrink-0 scale-90"
                />
              </div>

              <div className="grid grid-cols-2 gap-2.5 pt-1">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setCreateExamModalOpen(false)}
                  className="rounded-xl border-border/80 text-xs font-bold h-9 hover:bg-muted"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={!name.trim() || !code.trim() || submitting}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-xl text-xs font-bold h-9 shadow-2xs"
                >
                  {submitting ? 'Creating...' : 'Create Exam'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Exams List Container */}
        <Card className="rounded-2xl border border-border/80 bg-card shadow-glass-xs overflow-hidden">
          <CardHeader className="p-3.5 sm:p-4 pb-3 border-b border-border/60">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
              <div className="flex items-center gap-2">
                <CardTitle className="text-xs sm:text-sm font-bold text-foreground">
                  Configured Exams ({exams.length})
                </CardTitle>
                <Badge variant="outline" className="text-[10px] font-bold bg-muted/60 text-muted-foreground border-border">
                  {exams.filter(e => e.is_active).length} Active
                </Badge>
              </div>

              {/* Search input */}
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Filter exams..."
                  className="w-full h-8 pl-8 pr-7 text-xs rounded-xl bg-background border border-border/80 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary transition-all"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground cursor-pointer"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>
          </CardHeader>

          <CardContent className="p-0">
            {filteredExams.length === 0 ? (
              <div className="text-center py-10 px-4 space-y-2">
                <GraduationCap className="h-8 w-8 text-muted-foreground mx-auto opacity-50" />
                <p className="text-xs sm:text-sm text-muted-foreground">
                  {searchQuery ? `No exams matching "${searchQuery}"` : 'No exams created yet.'}
                </p>
                <Button
                  size="sm"
                  onClick={() => setCreateExamModalOpen(true)}
                  className="h-8 text-xs font-semibold rounded-xl mt-1"
                >
                  <Plus className="mr-1 h-3.5 w-3.5" /> Add First Exam
                </Button>
              </div>
            ) : (
              <>
                {/* Mobile View: Scannable Exam Cards (sm:hidden) */}
                <div className="divide-y divide-border/60 sm:hidden">
                  {filteredExams.map((e) => (
                    <div key={e.id} className="p-3.5 space-y-2.5 hover:bg-muted/20 transition-colors">
                      {/* Top row: Title & Badges */}
                      <div className="flex items-start justify-between gap-2">
                        <Link
                          href={`/admin/exams/${e.id}`}
                          className="font-bold text-xs text-foreground hover:text-primary transition-colors flex-1"
                        >
                          {e.name}
                        </Link>
                        <div className="flex items-center gap-1.5 shrink-0">
                          {e.year && (
                            <Badge variant="outline" className="text-[10px] font-bold px-1.5 py-0 bg-primary/10 text-primary border-primary/20">
                              {e.year}
                            </Badge>
                          )}
                          <Badge
                            variant="outline"
                            className={`text-[10px] font-bold px-1.5 py-0 capitalize ${
                              e.is_active
                                ? 'bg-secondary/15 text-secondary border-secondary/30'
                                : 'bg-muted text-muted-foreground border-border'
                            }`}
                          >
                            {e.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </div>
                      </div>

                      {/* Code and counts */}
                      <div className="flex items-center justify-between text-[11px] text-muted-foreground gap-2">
                        <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-muted/60 border border-border/60 text-foreground">
                          #{e.code}
                        </span>

                        <div className="flex items-center gap-2">
                          <span className="flex items-center gap-1 text-[11px]">
                            <BookOpen className="h-3 w-3 text-secondary" />
                            {e.syllabi_count} {e.syllabi_count === 1 ? 'syllabus' : 'syllabi'}
                          </span>
                          <span>•</span>
                          <span className="flex items-center gap-1 text-[11px]">
                            <FileText className="h-3 w-3 text-tertiary" />
                            {e.tests_count} {e.tests_count === 1 ? 'test' : 'tests'}
                          </span>
                        </div>
                      </div>

                      {/* Actions Footer */}
                      <div className="flex items-center justify-between pt-1 border-t border-border/40">
                        <Link
                          href={`/admin/exams/${e.id}`}
                          className="inline-flex items-center gap-1 text-[11px] font-semibold text-primary hover:underline"
                        >
                          <FolderTree className="h-3 w-3" />
                          <span>Manage Exam Blueprint</span>
                          <ChevronRight className="h-3 w-3 opacity-70" />
                        </Link>

                        <button
                          type="button"
                          onClick={() => handleDelete(e.id)}
                          className="p-1.5 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors cursor-pointer"
                          title="Delete Exam"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Desktop View: Polished Table (hidden sm:block) */}
                <div className="hidden sm:block">
                  <Table>
                    <TableHeader>
                      <TableRow className="hover:bg-transparent border-border/60">
                        <TableHead className="font-bold text-xs text-foreground">Exam Name</TableHead>
                        <TableHead className="font-bold text-xs text-foreground w-28">Code</TableHead>
                        <TableHead className="font-bold text-xs text-foreground w-24">Year</TableHead>
                        <TableHead className="font-bold text-xs text-foreground w-32">Syllabi</TableHead>
                        <TableHead className="font-bold text-xs text-foreground w-28">Tests</TableHead>
                        <TableHead className="font-bold text-xs text-foreground w-24">Status</TableHead>
                        <TableHead className="font-bold text-xs text-foreground w-28 text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredExams.map((e) => (
                        <TableRow key={e.id} className="border-border/50 hover:bg-muted/30">
                          <TableCell className="font-semibold text-xs py-3">
                            <Link
                              href={`/admin/exams/${e.id}`}
                              className="hover:underline flex items-center gap-2 text-foreground hover:text-primary transition-colors font-bold"
                            >
                              <FolderTree className="h-4 w-4 text-primary shrink-0" />
                              <span>{e.name}</span>
                            </Link>
                          </TableCell>
                          <TableCell className="py-3">
                            <span className="font-mono text-xs px-2 py-0.5 rounded bg-muted/60 border border-border/60 text-muted-foreground">
                              {e.code}
                            </span>
                          </TableCell>
                          <TableCell className="py-3">
                            {e.year ? (
                              <Badge variant="outline" className="text-xs font-semibold bg-primary/10 text-primary border-primary/20">
                                {e.year}
                              </Badge>
                            ) : (
                              <span className="text-xs text-muted-foreground italic">—</span>
                            )}
                          </TableCell>
                          <TableCell className="py-3">
                            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                              <BookOpen className="h-3.5 w-3.5 text-secondary" />
                              {e.syllabi_count} {e.syllabi_count === 1 ? 'syllabus' : 'syllabi'}
                            </span>
                          </TableCell>
                          <TableCell className="py-3">
                            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                              <FileText className="h-3.5 w-3.5 text-tertiary" />
                              {e.tests_count} {e.tests_count === 1 ? 'test' : 'tests'}
                            </span>
                          </TableCell>
                          <TableCell className="py-3">
                            <Badge
                              variant="outline"
                              className={`text-xs font-bold capitalize ${
                                e.is_active
                                  ? 'bg-secondary/15 text-secondary border-secondary/30'
                                  : 'bg-muted text-muted-foreground border-border'
                              }`}
                            >
                              {e.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </TableCell>
                          <TableCell className="py-3 text-right">
                            <div className="flex justify-end items-center gap-1">
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 px-2 text-xs font-semibold rounded-lg gap-1 border-border/80"
                                asChild
                              >
                                <Link href={`/admin/exams/${e.id}`}>
                                  <span>Manage</span>
                                  <ExternalLink className="h-3 w-3 opacity-70" />
                                </Link>
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-7 w-7 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-lg"
                                onClick={() => handleDelete(e.id)}
                                title="Delete Exam"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </AppShell>
  )
}
